import React, { useContext } from 'react';
import styled from 'styled-components';
import { LocaleContext } from '../../../contexts/LocaleContext';
import { useLocalizedNavigation } from '@repo/gatsby-localization-helpers';
import breakpoints from '../../breakpoints';
import PageWrapper from '../../page-wrapper';
import sharedStyles from '../../styles';
import Button from '../../button/Button';
import BlockContent from '../BlockContent';

type ThemeProps = {
  colors: {
    background: string;
    defaultFont: string;
    brandPrimary: string;
    brandSecondary: string;
  };
};

type Media = {
  alt?: string;
  asset?: {
    url: string;
  };
};

type Cta = {
  linkType?: 'internal' | 'external' | 'disable';
  internalLink?: string;
  href?: string;
  caption?: string;
  blank?: boolean;
};

type Item = {
  _key: string;
  copy?: object;
  media?: Media;
  cta?: Cta;
};

type FeatureSectionProps = {
  headline?: string;
  themeStyle?: 'primary' | 'secondary';
  items?: Item[];
};

interface ContainerProps {
  themeStyle?: 'primary' | 'secondary';
  theme?: ThemeProps;
}

interface BlockBuilderFeatureSectionProps {
  value: FeatureSectionProps;
  getInternalLink: (link: any) => string;
}

const Container = styled.div<ContainerProps>`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-block: ${sharedStyles.spacing['spacing-2xl']};

  h2 {
    text-align: center;
    margin: 0;
    margin-block: 0;
    margin-bottom: ${sharedStyles.spacing['spacing-lg']};

    @media screen and (min-width: ${breakpoints.md}) {
      margin-block: 0 ${sharedStyles.spacing['spacing-1xl']};
    }
  }

  ${({ theme, themeStyle }) => {
    if (themeStyle === 'secondary') {
      return {
        backgroundColor: theme?.colors.background,
        color: theme?.colors.defaultFont,
      };
    }

    return {
      backgroundColor: theme?.colors.brandPrimary,
      color: theme?.colors.brandSecondary,
      '*': { color: theme?.colors.brandSecondary },
    };
  }};
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${sharedStyles.spacing['spacing-2xl']} 0
    ${sharedStyles.spacing['spacing-md']};
  gap: ${sharedStyles.spacing['spacing-md']};
  justify-content: center;

  &:first-of-type {
    margin-top: ${sharedStyles.spacing['spacing-md']};
  }

  @media screen and (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  text-align: center;
  align-self: center;
  margin-block: ${sharedStyles.spacing['spacing-1xl']};
  margin: 0;

  img {
    max-height: 100px;
    max-width: 100%;
  }

  @media screen and (min-width: ${breakpoints.md}) {
    width: 10%;
    align-self: flex-start;
    margin-block: 0;
    margin: 0;
  }
`;

const CopyContainer = styled.div<{ hasImage?: boolean }>`
  width: 100%;
  max-width: 75ch;

  @media screen and (min-width: ${breakpoints.md}) {
    ${({ hasImage }) => (hasImage ? `width: 90%;` : `width: 100%;`)}
  }
`;

const BlockBuilderFeatureSection: React.FC<BlockBuilderFeatureSectionProps> = ({
  value,
  getInternalLink,
}) => {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';

  const navigate = useLocalizedNavigation();

  return (
    // This div wrapper gaurentees that no parent padding/margin applies.
    // This block is a full screen width block,
    // so we want to overflow every parents width constraints
    <div>
      <Container
        className="blockbuilder-feature-section"
        themeStyle={value?.themeStyle}
      >
        <PageWrapper background="transparent">
          {value?.headline && <h2>{value.headline}</h2>}
          {value?.items?.length > 0 &&
            value.items.map(item => (
              <InnerContainer key={item._key}>
                {item?.media?.asset?.url && (
                  <ImageContainer>
                    <img
                      src={item.media.asset.url}
                      alt={item.media.alt || ''}
                    />
                  </ImageContainer>
                )}

                <CopyContainer hasImage={Boolean(item?.media?.asset?.url)}>
                  {item?.copy && (
                    <BlockContent
                      blocks={[item.copy]}
                      getInternalLink={getInternalLink}
                    />
                  )}
                  {(item?.cta?.linkType === 'internal' ||
                    item?.cta?.linkType === 'external') && (
                    <Button
                      mobileFullWidth
                      importance={
                        value?.themeStyle === 'secondary'
                          ? 'primary'
                          : 'secondary'
                      }
                      onClick={() => {
                        if (item.cta?.linkType === 'internal') {
                          navigate(
                            getInternalLink({
                              ...item.cta,
                              reference: item.cta?.internalLink,
                              currency,
                            })
                          );
                        }

                        if (item.cta?.linkType === 'external') {
                          window
                            .open(
                              item.cta?.href,
                              item.cta?.blank ? '_blank' : '_self'
                            )
                            .focus();
                        }
                      }}
                    >
                      {item?.cta?.caption}
                    </Button>
                  )}
                </CopyContainer>
              </InnerContainer>
            ))}
        </PageWrapper>
      </Container>
    </div>
  );
};

export default BlockBuilderFeatureSection;
